import { AxiosProgressEvent, AxiosResponse } from 'axios';
import { aiApi } from '../utils/aiApi';
import { getQueryParamsFromObj } from '../utils/api';
import { ProxyEvaluationTypeEnum } from '../utils/constants';
import { Document } from '../types/models';
import { UploadSharepointDocumentToResourceRequest } from '../types/Dtos/request';
import {
  DeleteDocumentsResponse,
  RunAnalysisResponse,
  UploadDocumentsResponse,
  GetProxyIndexedDocumentsStatusResponse,
  UploadDocumentResponse,
} from '../types/Dtos/response';

export const uploadSingleDocument = async (
  file: File,
  type: ProxyEvaluationTypeEnum,
  resourceId: string,
  docIds?: string[],
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  replacements?: Record<string, string>,
): Promise<AxiosResponse<UploadDocumentResponse>> => {
  const response = await uploadDocuments(
    [file],
    type,
    resourceId,
    docIds,
    onUploadProgress,
    replacements,
  );
  const doc = response.data.documents[0];
  return {
    ...response,
    data: {
      id: doc?.docId ?? '',
      name: doc?.name ?? '',
      sizeInMegaBytes: doc?.sizeInMb ?? 0,
      isSuccess: doc?.isSuccess ?? false,
      error: doc?.error ?? { code: '', reasons: [] },
    },
  };
};

export const uploadDocuments = (
  files: File[],
  type: ProxyEvaluationTypeEnum,
  resourceId: string,
  docIds?: string[],
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  replacements?: Record<string, string>,
) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  if (docIds) {
    docIds.forEach((id) => formData.append('docIds', id));
  }
  if (replacements && Object.keys(replacements).length > 0) {
    formData.append('replacements', JSON.stringify(replacements));
  }

  const params = getQueryParamsFromObj({ type, resourceId });
  return aiApi.post<UploadDocumentsResponse>(`v1/documents/upload?${params}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
};

export const listDocuments = (
  type: ProxyEvaluationTypeEnum,
  resourceId: string,
  orderBy?: string,
  orderDirection?: string,
  searchText?: string,
  label?: string,
): Promise<AxiosResponse<Document[]>> => {
  const params = getQueryParamsFromObj({
    type,
    resourceId,
    orderBy,
    orderDirection,
    searchText,
    label,
  });
  return aiApi.get<Document[]>(`v1/documents?${params}`);
};

export const getDocumentByOrganization = (organizationId: string, documentId: string) =>
  aiApi.get<Document>(`v1/documents/organization/${organizationId}/document/${documentId}`);

export const getDocumentByOrganizationAndProject = (
  organizationId: string,
  projectId: string,
  documentId: string,
) =>
  aiApi.get<Document>(
    `v1/documents/organization/${organizationId}/project/${projectId}/document/${documentId}`,
  );

export const deleteDocuments = (
  type: ProxyEvaluationTypeEnum,
  resourceId: string,
  documentIds: string[],
  allDocumentsRemoved = false,
) => {
  const params = getQueryParamsFromObj({ type, resourceId });
  return aiApi.delete<DeleteDocumentsResponse>(`v1/documents?${params}`, {
    data: { documentIds, allDocumentsRemoved },
  });
};

export const runAnalysis = (
  type: ProxyEvaluationTypeEnum,
  resourceId: string,
  side: 'client' | 'supplier' = 'client',
) => {
  const params = getQueryParamsFromObj({ type, resourceId, side });
  return aiApi.post<RunAnalysisResponse>(`v1/documents/run_analysis?${params}`);
};

export const getIndexingStatus = (type: ProxyEvaluationTypeEnum, evalId: string) => {
  const params = getQueryParamsFromObj({ type, evalId });
  return aiApi.get<GetProxyIndexedDocumentsStatusResponse>(
    `v1/documents/indexing_status?${params}`,
  );
};

export const uploadSharepointDocuments = (
  type: ProxyEvaluationTypeEnum,
  resourceId: string,
  payload: UploadSharepointDocumentToResourceRequest,
) => {
  const params = getQueryParamsFromObj({ type, resourceId });
  return aiApi.post<UploadDocumentsResponse>(`v1/documents/sharepoint/upload?${params}`, payload);
};
